import { Marker } from 'react-map-gl/maplibre';
import { Plus, Flame, Shield } from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import type { NpcStation } from '@/state/types';

/**
 * Phase 7.2.4 — NPC stations available for acquisition. Rendered semi-transparent
 * with a "+" overlay. Click selects the NPC for the AcquireNpcDialog.
 *
 * Phase 11.2.3 (Bug N5) — While `buildMode` is active, NPC markers don't
 * intercept clicks. The user's intent is "place a new station here", and we
 * don't want a stray NPC under the cursor to hijack that.
 */

interface NpcStationMarkersProps {
  onSelect: (npc: NpcStation) => void;
}

const POLICE_KINDS = new Set([
  'police-station',
  'riot-police-station',
  'sek-base',
  'federal-police-station',
]);

export function NpcStationMarkers({ onSelect }: NpcStationMarkersProps) {
  const npcs = useGameStore((s) => s.npcStations);
  const visible = useGameStore((s) => s.uiPrefs.layerToggles.stations);
  const buildMode = useGameStore((s) => s.buildMode);
  // Phase 13.10 — NPC-Marker default ausgeblendet; nur bei explizitem Toggle.
  const showAcquirable = useGameStore((s) => s.uiPrefs.showAcquirableStations);
  if (!visible || !showAcquirable) return null;

  return (
    <>
      {npcs.map((npc) => {
        const isPolice = POLICE_KINDS.has(npc.kind);
        const Icon = isPolice ? Shield : npc.kind === 'hospital' ? Plus : Flame;
        const bg = isPolice
          ? '#1d4ed8'
          : npc.kind === 'hospital'
            ? '#374151'
            : npc.kind === 'ems-station'
              ? '#f97316'
              : npc.kind === 'bf-station'
                ? '#b91c1c'
                : '#dc2626';
        return (
          <Marker
            key={npc.id}
            longitude={npc.position.lng}
            latitude={npc.position.lat}
            anchor="bottom"
          >
            <button
              type="button"
              onClick={(e) => {
                // Phase 11.2.3 (N5): if a build-mode is active, fall through
                // to the map's click handler so the build placement runs.
                if (buildMode) return;
                e.stopPropagation();
                onSelect(npc);
              }}
              title={`${npc.name} – verfügbar`}
              aria-label={`${npc.name} – verfügbar`}
              className="relative flex items-center justify-center"
              style={{
                pointerEvents: buildMode ? 'none' : 'auto',
              }}
            >
              <span
                className={[
                  'flex h-7 w-7 items-center justify-center rounded-lg shadow transition',
                  buildMode ? 'opacity-30' : 'opacity-50 grayscale group-hover:opacity-80',
                ].join(' ')}
                style={{ background: bg, border: '2px dashed #ffffff' }}
              >
                <Icon className="h-3.5 w-3.5 text-white" aria-hidden />
              </span>
              <span
                className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-status-available text-[10px] font-bold text-bg-primary shadow"
                aria-hidden
              >
                +
              </span>
            </button>
          </Marker>
        );
      })}
    </>
  );
}
